import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCelebration } from '@/context/CelebrationContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Briefcase, Heart, Users, Sparkles, Moon, Eye,
  ArrowRight, ArrowLeft, Check, Star
} from 'lucide-react';

const API_URL = window.location.origin;

// Focus areas with their configurations
const FOCUS_AREAS = [
  {
    id: 'career',
    label: 'Career Clarity',
    description: 'Job transitions, leadership growth, or finding purpose in work',
    icon: Briefcase,
    color: 'bg-blue-500',
    gradient: 'from-blue-500 to-blue-600',
    communities: ['comm-ic-careers', 'comm-ic-resume'],
    videos: ['career-transitions', 'leadership-presence'],
    prompt: 'What would career fulfillment look like for you right now?'
  },
  {
    id: 'boundaries',
    label: 'Boundaries & Relationships',
    description: 'Setting limits, navigating difficult dynamics, finding peace',
    icon: Users,
    color: 'bg-pink-500',
    gradient: 'from-pink-500 to-rose-600',
    communities: ['comm-ic-relationships'],
    videos: ['healthy-boundaries', 'communication'],
    prompt: 'What relationship boundary would bring you the most peace?'
  },
  {
    id: 'faith',
    label: 'Faith & Discernment',
    description: 'Spiritual grounding, trusting God\'s timing, seeking wisdom',
    icon: Heart,
    color: 'bg-purple-500',
    gradient: 'from-purple-500 to-violet-600',
    communities: ['comm-ic-prayer'],
    videos: ['faith-journey', 'spiritual-discernment'],
    prompt: 'Where are you seeking God\'s guidance in your life right now?'
  },
  {
    id: 'rest',
    label: 'Rest & Nervous System',
    description: 'Burnout recovery, stress management, finding stillness',
    icon: Moon,
    color: 'bg-teal-500',
    gradient: 'from-teal-500 to-cyan-600',
    communities: ['comm-ic-main', 'comm-ic-wins'],
    videos: ['rest-recovery', 'stress-management'],
    prompt: 'What does rest look like for you? What\'s been depleting your energy?'
  },
  {
    id: 'visibility',
    label: 'Visibility & Leadership',
    description: 'Personal brand, showing up authentically, stepping into influence',
    icon: Eye,
    color: 'bg-amber-500',
    gradient: 'from-amber-500 to-orange-600',
    communities: ['comm-ic-content', 'comm-ic-wins'],
    videos: ['personal-branding', 'authentic-leadership'],
    prompt: 'How do you want to be seen? What holds you back from showing up fully?'
  }
];

const PersonalFocusPath = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { grandCelebrate } = useCelebration();
  const [step, setStep] = useState(1);
  const [selectedFocus, setSelectedFocus] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const token = localStorage.getItem('token');
  const totalSteps = 3;

  const handleSelectFocus = (focus) => {
    setSelectedFocus(focus);
  };

  const handleNext = () => {
    if (step === 1 && !selectedFocus) {
      toast.error('Please select a focus area');
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/user/focus-path`,
        {
          focus_area: selectedFocus.id,
          focus_label: selectedFocus.label,
          recommended_communities: selectedFocus.communities,
          journal_prompt: selectedFocus.prompt,
          started_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      grandCelebrate('sparkle', 'milestone');
      toast.success('Your sacred focus is set! Let your becoming begin.');
      onComplete?.(selectedFocus);
      onClose();
    } catch (error) {
      console.error('Failed to save focus path:', error);
      toast.error('Failed to save your focus. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGoToCommunity = (communityId) => {
    navigate(`/communities/${communityId}`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-terracotta" />
            {step === 1 && "Where Should You Focus This Month?"}
            {step === 2 && `Your ${selectedFocus?.label} Path`}
            {step === 3 && "You're Ready to Begin"}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="mt-2">
          <Progress value={(step / totalSteps) * 100} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">Step {step} of {totalSteps}</p>
        </div>

        {/* Step 1: Select Focus Area */}
        {step === 1 && (
          <div className="space-y-4 mt-4">
            <p className="text-gray-600">
              Let's set an intention for your first 30 days. What area of your life needs the most attention right now?
            </p>
            
            <div className="grid gap-3">
              {FOCUS_AREAS.map((focus) => (
                <button
                  key={focus.id}
                  onClick={() => handleSelectFocus(focus)}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    selectedFocus?.id === focus.id 
                      ? 'border-forest bg-forest/5' 
                      : 'border-sand hover:border-forest/30'
                  }`}
                  data-testid={`focus-${focus.id}`}
                >
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${focus.gradient} text-white`}>
                    <focus.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-forest">{focus.label}</p>
                    <p className="text-sm text-gray-500">{focus.description}</p>
                  </div>
                  {selectedFocus?.id === focus.id && (
                    <Check className="h-5 w-5 text-forest" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Show What's Ahead */}
        {step === 2 && selectedFocus && (
          <div className="space-y-4 mt-4">
            <p className="text-gray-600">
              Based on your focus, here's what we recommend for your journey:
            </p>

            {/* Recommended Communities */}
            <Card className="border-forest/20">
              <CardContent className="p-4">
                <h3 className="font-medium text-forest mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Communities to Join
                </h3>
                <div className="space-y-2">
                  {selectedFocus.communities.map((commId) => {
                    const communityNames = {
                      'comm-ic-main': 'Inner Circle Community',
                      'comm-ic-careers': 'Career Opportunities',
                      'comm-ic-resume': 'Resume & LinkedIn Support',
                      'comm-ic-relationships': 'Relationships & Boundaries',
                      'comm-ic-prayer': 'Prayer & Encouragement',
                      'comm-ic-wins': 'Wins & Accountability',
                      'comm-ic-content': 'Content & Visibility'
                    };
                    return (
                      <div key={commId} className="flex items-center justify-between p-2 bg-sand/30 rounded-lg">
                        <span className="text-sm text-forest">{communityNames[commId] || commId}</span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-xs"
                          onClick={() => handleGoToCommunity(commId)}
                        >
                          Preview
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Journal Prompt */}
            <Card className="border-terracotta/20 bg-terracotta/5">
              <CardContent className="p-4">
                <h3 className="font-medium text-forest mb-2 flex items-center gap-2">
                  <Star className="h-4 w-4 text-terracotta" /> Your First Reflection
                </h3>
                <p className="text-sm text-gray-600 italic">
                  "{selectedFocus.prompt}"
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  This will be waiting for you in your journal.
                </p>
              </CardContent>
            </Card>

            {/* What to Expect */}
            <div className="p-4 bg-sand/30 rounded-xl">
              <h3 className="font-medium text-forest mb-2">What to Expect</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-forest rounded-full mt-2" />
                  Highlighted communities relevant to your focus
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-forest rounded-full mt-2" />
                  Suggested videos from our library
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-forest rounded-full mt-2" />
                  Weekly journal prompts tailored to you
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-forest rounded-full mt-2" />
                  Gentle check-ins (no pressure, just care)
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && selectedFocus && (
          <div className="space-y-4 mt-4 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${selectedFocus.gradient} flex items-center justify-center`}>
              <selectedFocus.icon className="h-10 w-10 text-white" />
            </div>
            
            <div>
              <h3 className="font-heading text-2xl text-forest mb-2">
                Your Focus: {selectedFocus.label}
              </h3>
              <p className="text-gray-600">
                You've chosen to focus on what matters most to you right now. We're here to support you every step of the way.
              </p>
            </div>

            <div className="p-4 bg-cream rounded-xl text-left">
              <p className="text-sm text-forest font-medium mb-2">Remember:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Go at your own pace</li>
                <li>• You can change your focus anytime</li>
                <li>• This is your space to be supported</li>
                <li>• Someone here knows your name</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t border-sand">
          {step > 1 ? (
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Skip for now
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button 
              onClick={handleNext} 
              disabled={step === 1 && !selectedFocus}
              className="bg-forest hover:bg-forest/90"
            >
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleComplete}
              disabled={saving}
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {saving ? 'Setting up...' : 'Begin My Journey'} 
              {!saving && <Sparkles className="h-4 w-4 ml-2" />}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalFocusPath;
